import React from "react";
import { Link } from "react-router-dom";
import OTPScreen2 from "./OtpScreen2";

// Template8 — frosted-glass card centered over a full-screen internet image.
// Generic (no company branding), self-contained styling.
const BG_IMAGE =
  "https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=1600&q=80";

const styles = `
.tmpl8-root{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;position:relative;overflow:hidden;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;}
.tmpl8-root *{box-sizing:border-box;}
.tmpl8-bg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;}
.tmpl8-bg-overlay{position:absolute;inset:0;background:linear-gradient(135deg,rgba(30,27,75,.72),rgba(15,23,42,.78));z-index:1;}
.tmpl8-card{position:relative;z-index:2;width:100%;max-width:420px;padding:44px 38px;border-radius:24px;background:rgba(255,255,255,.12);backdrop-filter:blur(22px);-webkit-backdrop-filter:blur(22px);border:1px solid rgba(255,255,255,.22);box-shadow:0 24px 60px rgba(0,0,0,.35);color:#fff;}
.tmpl8-logo{width:52px;height:52px;border-radius:16px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#818cf8,#c084fc);box-shadow:0 10px 24px rgba(129,140,248,.5);margin-bottom:22px;}
.tmpl8-title{font-size:26px;font-weight:700;margin:0 0 6px;letter-spacing:-.4px;color:#fff;}
.tmpl8-sub{font-size:14px;color:rgba(255,255,255,.72);margin:0 0 30px;}
.tmpl8-field{margin-bottom:18px;}
.tmpl8-label{display:block;font-size:12.5px;font-weight:600;color:rgba(255,255,255,.85);margin-bottom:7px;letter-spacing:.2px;}
.tmpl8-input-wrap{position:relative;}
.tmpl8-input{width:100%;height:48px;border:1px solid rgba(255,255,255,.25);border-radius:12px;padding:0 44px 0 16px;font-size:15px;color:#fff;background:rgba(255,255,255,.08);transition:border-color .15s,box-shadow .15s,background .15s;outline:none;}
.tmpl8-input::placeholder{color:rgba(255,255,255,.55);}
.tmpl8-input:focus{border-color:#c084fc;background:rgba(255,255,255,.14);box-shadow:0 0 0 4px rgba(192,132,252,.18);}
.tmpl8-eye{position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:rgba(255,255,255,.6);padding:6px;display:flex;align-items:center;}
.tmpl8-eye:hover{color:#fff;}
.tmpl8-options{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;min-height:20px;}
.tmpl8-check{display:flex;align-items:center;gap:8px;font-size:13.5px;color:rgba(255,255,255,.82);cursor:pointer;user-select:none;}
.tmpl8-check input{width:16px;height:16px;accent-color:#c084fc;cursor:pointer;}
.tmpl8-link{font-size:13.5px;color:#e9d5ff;font-weight:600;text-decoration:none;}
.tmpl8-link:hover{text-decoration:underline;}
.tmpl8-btn{width:100%;height:50px;border:none;border-radius:12px;background:linear-gradient(135deg,#818cf8,#c084fc);color:#fff;font-size:16px;font-weight:600;cursor:pointer;transition:transform .12s,box-shadow .2s,opacity .2s;box-shadow:0 12px 26px rgba(129,140,248,.4);display:flex;align-items:center;justify-content:center;gap:8px;}
.tmpl8-btn:hover{transform:translateY(-1px);box-shadow:0 16px 34px rgba(129,140,248,.55);}
.tmpl8-btn:active{transform:translateY(0);}
.tmpl8-btn:disabled{opacity:.55;cursor:not-allowed;box-shadow:none;transform:none;}
.tmpl8-recaptcha{margin-bottom:18px;display:flex;justify-content:center;}
.tmpl8-otp-wrap{margin-top:8px;}
`;

const LogoMark = () => (
  <span className="tmpl8-logo">
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path d="M12 2l2.6 6.8L21 11l-6.4 2.2L12 20l-2.6-6.8L3 11l6.4-2.2z" fill="#fff" />
    </svg>
  </span>
);

const EyeIcon = ({ off }) =>
  off ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

class Template8 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      isChecked: false,
      isShowPassword: false,
      twoFAStatus: null,
    };
  }

  updateTwoFAStatus = (t) => {
    this.setState({ twoFAStatus: t });
  };

  onLogin = () => {
    const { email, password, isChecked } = this.state;
    const { authenticationCallback, itemProperties, sessionId, twoFA } = this.props;
    if (isChecked && email !== "") {
      localStorage.username = email;
      localStorage.password = password;
      localStorage.checkbox = isChecked;
    }
    const payload = { email, password };

    if (twoFA) {
      this.props.setError(false);
      this.props.login(payload, () => {
        this.updateTwoFAStatus("OTP");
      });
    } else {
      if (itemProperties && itemProperties.authenticateUsingPlugins) {
        payload["authenticateUsingPlugins"] = true;
        payload["sessionId"] = sessionId;
      }
      this.props.setError(false);
      this.props.login(payload, authenticationCallback);
    }
  };

  togglePasswordVisiblity = () => {
    this.setState({ isShowPassword: !this.state.isShowPassword });
  };

  onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.onLogin();
    }
  };

  render() {
    const {
      label,
      hasForgotPassword,
      authTemplateTopFormText,
      itemProperties,
      authenticationCallback,
      reActivateUser, 
      reActivateUserEmailDisable
    } = this.props;

    const rememberMe =
      itemProperties && itemProperties.authConfig && itemProperties.authConfig.rememberMe;
    const otpScreenLabel = itemProperties && itemProperties.otpScreenLabel;
    const isOtp = this.state.twoFAStatus === "OTP";

    return (
      <div className="tmpl8-root">
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <img className="tmpl8-bg" src={BG_IMAGE} alt="" />
        <div className="tmpl8-bg-overlay" />

        <div className="tmpl8-card">
          <LogoMark />
          {isOtp ? (
            <>
              <h2 className="tmpl8-title">
                {otpScreenLabel ? otpScreenLabel : "One-Time Password"}
              </h2>
              <p className="tmpl8-sub">
                Enter the verification code we just sent you.
              </p>
              <div className="tmpl8-otp-wrap">
                <OTPScreen2
                  authenticationCallback={authenticationCallback}
                  requiredRole={itemProperties && itemProperties.requiredRole}
                  phoneNumber={this.state.email}
                />
              </div>
            </>
          ) : (
            <>
              <h2 className="tmpl8-title">
                {authTemplateTopFormText || "Welcome back"}
              </h2>
              <p className="tmpl8-sub">Sign in to continue to your account.</p>

              <div className="tmpl8-field">
                <label className="tmpl8-label" htmlFor="tmpl8-email">Email</label>
                <div className="tmpl8-input-wrap">
                  <input
                    id="tmpl8-email"
                    className="tmpl8-input"
                    value={this.state.email}
                    onChange={(e) => this.setState({ email: e.target.value })}
                    onKeyDown={this.onKeyDown}
                    placeholder="you@example.com"
                    type="text"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="tmpl8-field">
                <label className="tmpl8-label" htmlFor="tmpl8-password">Password</label>
                <div className="tmpl8-input-wrap">
                  <input
                    id="tmpl8-password"
                    className="tmpl8-input"
                    value={this.state.password}
                    onChange={(e) => this.setState({ password: e.target.value })}
                    onKeyDown={this.onKeyDown}
                    placeholder="Enter your password"
                    type={this.state.isShowPassword ? "text" : "password"}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="tmpl8-eye"
                    onClick={this.togglePasswordVisiblity}
                    aria-label={this.state.isShowPassword ? "Hide password" : "Show password"}
                  >
                    <EyeIcon off={this.state.isShowPassword} />
                  </button>
                </div>
              </div>

              <div className="tmpl8-options">
                {rememberMe ? (
                  <label className="tmpl8-check">
                    <input
                      type="checkbox"
                      checked={this.state.isChecked}
                      onChange={(e) => this.setState({ isChecked: e.target.checked })}
                    />
                    Remember me
                  </label>
                ) : (
                  <span />
                )}
                {reActivateUser && (
                      <>
                        <Link className="tmpl8-link"
                          to={{
                            pathname: "/app/reactivateuser",
                            state: {
                              doNotSendEmail: reActivateUserEmailDisable,
                            },
                          }}
                        >
                          Reactivate User
                        </Link>
                      </>
                    )}
                {hasForgotPassword ? (
                  <Link className="tmpl8-link" to="/app/forgotpassword">
                    Forgot password?
                  </Link>
                ) : (
                  <span />
                )}
              </div>

              {this.props.reCaptchaComponent ? (
                <div className="tmpl8-recaptcha">{this.props.reCaptchaComponent}</div>
              ) : null}

              <button
                type="button"
                className="tmpl8-btn"
                disabled={this.props.displayReCaptcha}
                onClick={this.onLogin}
              >
                {label && label.show ? label.text : "Sign in"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default Template8;
